import React, { useRef, useMemo, useCallback, useState, useEffect } from 'react'
import Map, { FullscreenControl, GeolocateControl, NavigationControl, ScaleControl } from 'react-map-gl/maplibre';
import { Source, Layer } from 'react-map-gl';
import { range } from 'd3-array';
import { scaleQuantile } from 'd3-scale';
import bbox from '@turf/bbox';
import { SimpleLoading } from '..';
import { usePositionedViewport } from '../../../hooks';
import { CatalogService, LocalStorageService } from '../../../services';
import { useDebounce } from '@uidotdev/usehooks';
import { Loader } from '@progress/kendo-react-indicators';
import { polygon } from '@turf/helpers';
import booleanContains from '@turf/boolean-contains';

const url = process.env.REACT_APP_ETICKET_API;
const catalogService = new CatalogService(url)
const localStorageService = new LocalStorageService();
let token = localStorageService.getLocalStorageItem("token")
if (token) token = token.idToken

const fillLayer = {
    type: 'fill',
    paint: {
        'fill-outline-color': '#0040c8',
        'fill-opacity': ["case", ["==", ["get", "percentile"], null], 0, 0.7],
        'fill-color': {
            property: 'percentile',
            stops: [
                [0, '#D7F4DF'],
                [1, '#7AD3A2'],
                [2, '#36AA9D'],
                [3, '#2B7D96'],
                [4, '#2B508C'],
                [5, '#322B68'],
                [6, '#22152E'],
                [7, '#0B0506']
            ]
        },
    }
};

function updatePercentiles(mapData, features, scale) {
    return {
        type: 'FeatureCollection',
        features: features.map(f => {
            const value = mapData.find(item => item.postalCode?.toString() === f.properties.d_code);

            const properties = {
                ...f.properties,
                value: value ? value.sum : null,
                percentile: value ? scale(value.sum) : null,
                tickets: value ? value.ticket_count : null,
                clients: value ? value.client_count : null
            };
            return { ...f, properties };
        })
    };
}

function CPChoroplethMap(props) {
    const { isLoading, data, t } = props;
    const [loadingGeojson, setLoadingGeojson] = useState(true)
    const [geojsonData, setGeojsonData] = useState()

    const mapRef = useRef();
    const initialViewport = usePositionedViewport();
    const [hoverInfo, setHoverInfo] = useState(null);
    const [currentBounds, setCurrentBounds] = useState();
    const debouncedBounds = useDebounce(currentBounds, 1000);

    const scale = useMemo(() => {
        if (!data || data.length === 0) return null;
        return scaleQuantile().domain(data.map(item => item.sum)).range(range(8))
    }, [data]);

    const sources = useMemo(() => {
        if (!data || data.length === 0 || !scale || !geojsonData) return null;

        const ponderedData = updatePercentiles(data, geojsonData, scale);

        return (
            <Source key={"cp-choropleth-map"} type="geojson" data={ponderedData}>
                <Layer id={"cp-choropleth-map"} {...fillLayer} />
            </Source>
        )
    }, [data, scale, geojsonData])

    const getGeojson = async () => {
        if (!data || !data.length) {
            setGeojsonData()
        } else {
            setLoadingGeojson(true)
            const geojson = await catalogService.getGeojsonData(token, debouncedBounds)
            setGeojsonData(geojson)
        }
        setLoadingGeojson(false)
    }

    useEffect(() => {
        getGeojson()
    }, [data])

    const onClick = useCallback((event) => {
        if (mapRef.current && event.features && event.features.length > 0) {
            const feature = event.features[0];
            if (feature) {
                const [minLng, minLat, maxLng, maxLat] = bbox(feature);

                mapRef.current.fitBounds(
                    [
                        [minLng, minLat],
                        [maxLng, maxLat]
                    ],
                    { padding: 40, duration: 1000 }
                );
            }
        }
    }, [])

    const onHover = useCallback((event) => {
        const {
            features,
            point: { x, y }
        } = event;
        const hoveredFeature = features && features[0];
        setHoverInfo(hoveredFeature ? { feature: hoveredFeature, x, y } : null);
    }, []);

    const getBounds = () => {
        if (!mapRef || !mapRef.current) return;

        const currentZoom = mapRef.current.getZoom();
        if (currentZoom < 8.5) return; // avoid fetching too many zip codes

        const canvas = mapRef.current.getCanvas(),
            w = canvas.width,
            h = canvas.height,
            cUL = mapRef.current.unproject([0, 0]).toArray(),
            cUR = mapRef.current.unproject([w, 0]).toArray(),
            cLR = mapRef.current.unproject([w, h]).toArray(),
            cLL = mapRef.current.unproject([0, h]).toArray();

        const coordinates = [cUL, cUR, cLR, cLL, cUL];
        if (!currentBounds) {
            setCurrentBounds(coordinates)
            return;
        }

        // check if the new viewport is inside the previous one
        const prevPolygon = polygon([currentBounds])
        const newPolygon = polygon([coordinates])
        if (!booleanContains(prevPolygon, newPolygon)) {
            setCurrentBounds(coordinates)
        }
    }

    useEffect(() => {
        getGeojson()
    }, [debouncedBounds])

    if (isLoading || !initialViewport) return <SimpleLoading />

    return (
        <>
            <Map
                ref={mapRef}
                reuseMaps
                initialViewState={initialViewport}
                style={{ height: '100%', width: '100%' }}
                mapStyle="https://api.maptiler.com/maps/basic-v2/style.json?key=zQZLhSau046UB7IrjeYK"
                interactiveLayerIds={["cp-choropleth-map"]}
                onClick={onClick}
                onMouseMove={onHover}
                onMoveEnd={getBounds}
            >
                {
                    loadingGeojson &&
                    <div className='dashboardMap-loading'>
                        <Loader size="small" type={'infinite-spinner'} /><span>{t("loading")}...</span>
                    </div>
                }
                {
                    mapRef.current?.getZoom() < 8.5 &&
                    <div className='dashboardMap-loading'>
                        <i className="k-icon k-i-warning"> </i><span>{t("zoomCloser")}</span>
                    </div>
                }

                <GeolocateControl position="top-left" showAccuracyCircle={false} />
                <FullscreenControl position="top-left" />
                <NavigationControl position="top-left" />
                <ScaleControl />

                {sources}

                {hoverInfo && (
                    <div className="dashboardMap-tooltip" style={{ left: hoverInfo.x, top: hoverInfo.y }}>
                        <ul>
                            <li><strong>{t("zipCode")}</strong>: {hoverInfo.feature.properties.d_code}</li>
                            <li><strong>{t("totalSales")}</strong>: {hoverInfo.feature.properties.value ? Number(hoverInfo.feature.properties.value).toFixed(2) : 0}</li>
                            <li><strong>{t("ticketCount")}</strong>: {hoverInfo.feature.properties.tickets ?? 0}</li>
                            <li><strong>{t("clientCount")}</strong>: {hoverInfo.feature.properties.clients ?? 0}</li>
                        </ul>
                    </div>
                )}
            </Map>
        </>
    )
}

export default CPChoroplethMap